<template>
  <div class="ft-page">
    <!-- Top navbar -->
    <div class="ft-nav">
      <div class="ft-nav__logo">
        <img src="/images/taxpod_logo.png" alt="taxPOD" class="ft-nav__logo-img" />
      </div>
    </div>

    <!-- Main content -->
    <div class="ft-main">
      <!-- Left panel -->
      <div class="ft-left">
        <div class="ft-left__bg">
          <div class="geo geo--1"></div>
          <div class="geo geo--2"></div>
          <div class="geo geo--3"></div>
          <div class="geo geo--4"></div>
        </div>
        <div class="ft-left__content">
          <h1 class="ft-left__title">
            Almost There!<br />
            Secure Your<br />
            Account With A<br />
            Strong Password
          </h1>
        </div>
      </div>

      <!-- Right panel -->
      <div class="ft-right">
        <div class="ft-right__wave">
          <svg viewBox="0 0 600 800" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M600 800V0C600 0 580 200 500 350C420 500 300 550 250 650C200 750 300 800 600 800Z" fill="rgba(56,189,248,0.06)"/>
            <path d="M600 800V100C600 100 560 300 480 420C400 540 320 580 280 680C240 780 350 800 600 800Z" fill="rgba(56,189,248,0.04)"/>
          </svg>
        </div>

        <!-- Card -->
        <div class="ft-card">
          <router-link
            :to="{
              name: 'PageGuestLoginFirstTime',
              params: { email: form.email },
            }"
            class="ft-card__back"
          >
            <v-icon size="18" color="var(--app-text-muted)">mdi-arrow-left</v-icon>
            <span>Back to Verification</span>
          </router-link>

          <div class="ft-card__icon-wrap">
            <v-icon size="28" color="var(--app-accent)">mdi-shield-lock-outline</v-icon>
          </div>

          <h2 class="ft-card__title">Set Your Password</h2>
          <p class="ft-card__subtitle">
            Create a strong password to secure your account. Make sure it's
            something you'll remember.
          </p>

          <transition name="fade">
            <div v-if="api.isError" class="ft-alert ft-alert--error">
              <v-icon size="18" color="#dc2626" class="ft-alert__icon">mdi-alert-circle-outline</v-icon>
              <span>{{ api.error }}</span>
            </div>
          </transition>

          <transition name="fade">
            <div v-if="localError" class="ft-alert ft-alert--error">
              <v-icon size="18" color="#dc2626" class="ft-alert__icon">mdi-alert-circle-outline</v-icon>
              <span>{{ localError }}</span>
            </div>
          </transition>

          <!-- Email (read-only) -->
          <div class="field">
            <label class="field__label">Email Address</label>
            <div class="field__input-wrap">
              <v-icon size="18" color="var(--app-text-faint)" class="field__icon">mdi-email-outline</v-icon>
              <input
                type="email"
                class="field__input field__input--icon field__input--readonly"
                v-model="form.email"
                readonly
                disabled
              />
            </div>
          </div>

          <!-- New Password -->
          <div class="field">
            <label class="field__label">New Password</label>
            <div class="field__input-wrap">
              <v-icon size="18" color="var(--app-text-faint)" class="field__icon">mdi-lock-outline</v-icon>
              <input
                :type="showPassword ? 'text' : 'password'"
                class="field__input field__input--icon field__input--toggle"
                placeholder="Enter a strong password"
                v-model="form.password"
              />
              <button
                type="button"
                class="field__toggle"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
              >
                <v-icon size="18" color="var(--app-text-faint)">
                  {{ showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
                </v-icon>
              </button>
            </div>

            <!-- Strength indicator -->
            <div v-if="form.password" class="strength">
              <div class="strength__bars">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="strength__bar"
                  :class="{
                    'strength__bar--active': i <= passwordStrength.score,
                    [`strength__bar--${passwordStrength.level}`]: i <= passwordStrength.score,
                  }"
                ></div>
              </div>
              <span class="strength__label" :class="`strength__label--${passwordStrength.level}`">
                {{ passwordStrength.label }}
              </span>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="field">
            <label class="field__label">Confirm New Password</label>
            <div class="field__input-wrap">
              <v-icon size="18" color="var(--app-text-faint)" class="field__icon">mdi-lock-check-outline</v-icon>
              <input
                :type="showPasswordConfirm ? 'text' : 'password'"
                class="field__input field__input--icon field__input--toggle"
                placeholder="Re-enter your password"
                v-model="form.passwordConfirmation"
                @keyup.enter="submit"
              />
              <button
                type="button"
                class="field__toggle"
                @click="showPasswordConfirm = !showPasswordConfirm"
                :aria-label="showPasswordConfirm ? 'Hide password' : 'Show password'"
              >
                <v-icon size="18" color="var(--app-text-faint)">
                  {{ showPasswordConfirm ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
                </v-icon>
              </button>
            </div>
            <div
              v-if="form.passwordConfirmation"
              class="match-hint"
              :class="passwordsMatch ? 'match-hint--ok' : 'match-hint--no'"
            >
              <v-icon size="14" :color="passwordsMatch ? '#10b981' : '#dc2626'">
                {{ passwordsMatch ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              <span>{{ passwordsMatch ? 'Passwords match' : "Passwords don't match" }}</span>
            </div>
          </div>

          <!-- Requirements checklist -->
          <ul class="reqs">
            <li :class="{ 'reqs__item--ok': checks.minLength }">
              <v-icon size="14" :color="checks.minLength ? '#10b981' : '#cbd5e1'">
                {{ checks.minLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>At least 8 characters</span>
            </li>
            <li :class="{ 'reqs__item--ok': checks.hasUpper }">
              <v-icon size="14" :color="checks.hasUpper ? '#10b981' : '#cbd5e1'">
                {{ checks.hasUpper ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>One uppercase letter</span>
            </li>
            <li :class="{ 'reqs__item--ok': checks.hasNumber }">
              <v-icon size="14" :color="checks.hasNumber ? '#10b981' : '#cbd5e1'">
                {{ checks.hasNumber ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>One number</span>
            </li>
            <li :class="{ 'reqs__item--ok': checks.hasSpecial }">
              <v-icon size="14" :color="checks.hasSpecial ? '#10b981' : '#cbd5e1'">
                {{ checks.hasSpecial ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>One special character</span>
            </li>
          </ul>

          <!-- Button -->
          <button
            class="ft-btn"
            :disabled="api.isLoading"
            @click="submit()"
          >
            <span v-if="!api.isLoading">Continue</span>
            <span v-else class="ft-btn__loading">
              <v-progress-circular indeterminate size="18" width="2" color="white"></v-progress-circular>
              Setting password...
            </span>
          </button>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import Api from "@/objects/api";
import HrdcData from "@/services/hrdc/objects/globalData";

export default {
  props: {
    email: {
      type: String,
    },
  },
  data: () => ({
    form: {
      email: null,
      password: null,
      passwordConfirmation: null,
    },
    api: new Api(),
    hrdcData: new HrdcData(),
    showPassword: false,
    showPasswordConfirm: false,
    localError: null,
  }),
  computed: {
    checks() {
      const pw = this.form.password || "";
      return {
        minLength: pw.length >= 8,
        hasUpper: /[A-Z]/.test(pw),
        hasNumber: /[0-9]/.test(pw),
        hasSpecial: /[^A-Za-z0-9]/.test(pw),
      };
    },
    passwordStrength() {
      const score = Object.values(this.checks).filter(Boolean).length;
      const map = {
        0: { label: "Too weak", level: "weak" },
        1: { label: "Weak", level: "weak" },
        2: { label: "Fair", level: "fair" },
        3: { label: "Good", level: "good" },
        4: { label: "Strong", level: "strong" },
      };
      return { score, ...map[score] };
    },
    passwordsMatch() {
      return (
        this.form.password &&
        this.form.passwordConfirmation &&
        this.form.password === this.form.passwordConfirmation
      );
    },
  },
  watch: {
    "form.password"() {
      this.localError = null;
    },
    "form.passwordConfirmation"() {
      this.localError = null;
    },
  },
  created() {
    if (!this.email) this.$router.push({ name: "PageGuestLoginFirstTime" });
    this.form.email = this.$_.clone(this.email);
    this.api.setMethod(`POST`);
    this.api.setUrl(`${this.$service.sso}/v1/en/auth/login/password/set`);
    this.api.setCallbackCompleted((resp) => {
      this.$store.commit("updateAuth", resp);
      this.$router.push({ name: "PageConsoleDashboard" });
    });
  },
  methods: {
    submit() {
      this.localError = null;
      if (!this.form.password || !this.form.passwordConfirmation) {
        this.localError = "Please fill in both password fields.";
        return;
      }
      if (this.form.password !== this.form.passwordConfirmation) {
        this.localError = "Passwords don't match.";
        return;
      }
      this.api.setParams(this.form);
      this.api.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .ft-nav {
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .ft-card {
  background: var(--app-card);
}

.dark-mode .ft-card__back {
  color: var(--app-text-muted);
}

.dark-mode .ft-card__title {
  color: var(--app-text);
}

.dark-mode .ft-card__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .ft-alert--error {
  background: var(--app-danger-bg);
}

.dark-mode .field__label {
  color: var(--app-text);
}

.dark-mode .field__input {
  border: 1.5px solid var(--app-border-strong);
  color: var(--app-text);
  background: var(--app-card);
}

.dark-mode .field__input--readonly {
  background: var(--app-surface);
  color: var(--app-text-muted);
}

.dark-mode .field__input:disabled:focus {
  border-color: var(--app-border-strong);
}

.dark-mode .field__toggle:hover {
  background: var(--app-elevated);
}

.dark-mode .strength__bar {
  background: var(--app-elevated);
}

.dark-mode .reqs {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .reqs li {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

.ft-page {
  /* `zoom: 1.1` below scales this box after layout, so a plain 100vh paints at
     110vh and the bottom tenth — the footer — falls off screen behind
     `overflow: hidden`. Dividing by the zoom makes the painted height 100vh. */
  height: calc(100vh / 1.1);
  display: flex;
  flex-direction: column;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f0f4f8;
  zoom: 1.1;
  overflow: hidden;
}

/* Navbar */
.ft-nav {
  height: 56px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 32px;
  border-bottom: 1px solid #e8eaed;
  flex-shrink: 0;
  z-index: 10;
}

.ft-nav__logo-img {
  height: 32px;
  object-fit: contain;
}

/* Main */
.ft-main {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* Left */
.ft-left {
  flex: 0 0 42%;
  background: linear-gradient(160deg, #2c4a7c, #345a8c, #3a6a9e);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.ft-left__bg { position: absolute; inset: 0; }

.geo { position: absolute; background: rgba(255,255,255,0.04); }
.geo--1 { width: 300px; height: 300px; top: -50px; left: -80px; transform: rotate(45deg); border: 1px solid rgba(255,255,255,0.06); }
.geo--2 { width: 200px; height: 200px; top: 30%; left: 25%; transform: rotate(30deg); background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); }
.geo--3 { width: 250px; height: 250px; bottom: -60px; left: 10%; transform: rotate(60deg); border: 1px solid rgba(255,255,255,0.05); }
.geo--4 { width: 150px; height: 150px; top: 15%; right: -30px; transform: rotate(15deg); background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); }

.ft-left__content { position: relative; z-index: 1; padding: 60px; }
.ft-left__title { font-size: 1.8rem; font-weight: 800; color: #fff; line-height: 1.35; margin: 0; }

/* Right */
.ft-right {
  flex: 1;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 40px;
}

.ft-right__wave { position: absolute; right: 0; bottom: 0; width: 60%; height: 100%; pointer-events: none; }
.ft-right__wave svg { width: 100%; height: 100%; }

/* Card */
.ft-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  border: 1px solid #e4e8ec;
  position: relative;
  z-index: 1;
}

.ft-card__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
  margin-bottom: 24px;
  transition: color 0.15s;
}

.ft-card__back:hover { color: #0ea5e9; }

.ft-card__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #f0f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.ft-card__title { font-size: 1.3rem; font-weight: 700; color: #1e293b; margin: 0 0 6px; }
.ft-card__subtitle { font-size: 0.82rem; color: #94a3b8; margin: 0 0 24px; font-weight: 400; line-height: 1.5; }

/* Alert */
.ft-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.82rem;
  font-weight: 500;
}

.ft-alert--error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.ft-alert__icon { flex-shrink: 0; }

/* Field */
.field { margin-bottom: 18px; }
.field__label { display: block; font-size: 0.82rem; font-weight: 600; color: #334155; margin-bottom: 6px; }
.field__input-wrap { position: relative; }
.field__icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); pointer-events: none; }

.field__input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.88rem;
  font-family: 'Lato', sans-serif;
  color: #1e293b;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.field__input--icon { padding-left: 42px; }
.field__input--toggle { padding-right: 42px; }
.field__input--readonly {
  background: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
}
.field__input::placeholder { color: #b0b8c1; }
.field__input:focus { border-color: #60b8e0; box-shadow: 0 0 0 3px rgba(96,184,224,0.1); }
.field__input:disabled:focus { border-color: #d1d5db; box-shadow: none; }

.field__toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.field__toggle:hover { background: #f1f5f9; }

/* Strength indicator */
.strength {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}
.strength__bars {
  display: flex;
  gap: 4px;
  flex: 1;
}
.strength__bar {
  flex: 1;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  transition: background 0.2s ease;
}
.strength__bar--weak { background: #ef4444; }
.strength__bar--fair { background: #f59e0b; }
.strength__bar--good { background: #3b82f6; }
.strength__bar--strong { background: #10b981; }
.strength__label {
  font-size: 0.72rem;
  font-weight: 700;
  min-width: 56px;
  text-align: right;
}
.strength__label--weak { color: #ef4444; }
.strength__label--fair { color: #f59e0b; }
.strength__label--good { color: #3b82f6; }
.strength__label--strong { color: #10b981; }

/* Match hint */
.match-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  font-size: 0.74rem;
  font-weight: 600;
}
.match-hint--ok { color: #10b981; }
.match-hint--no { color: #dc2626; }

/* Requirements */
.reqs {
  list-style: none;
  padding: 12px 14px;
  margin: 0 0 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 14px;
}
.reqs li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  font-weight: 500;
  color: #94a3b8;
  transition: color 0.15s;
}
.reqs__item--ok { color: #10b981 !important; }

/* Button */
.ft-btn {
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, #5ab8e0, #60c4ec);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ft-btn:hover { background: linear-gradient(135deg, #4aa8d0, #55b8e2); box-shadow: 0 4px 14px rgba(90,184,224,0.25); }
.ft-btn:disabled { opacity: 0.7; cursor: wait; }
.ft-btn__loading { display: flex; align-items: center; justify-content: center; gap: 10px; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Mobile */
@media (max-width: 768px) {
  .ft-main { flex-direction: column; }
  .ft-left { flex: none; min-height: 160px; }
  .ft-left__content { padding: 32px 28px; }
  .ft-left__title { font-size: 1.2rem; }
  .ft-right { padding: 28px 20px; }
  .ft-card { max-width: 100%; padding: 24px; }
  .reqs { grid-template-columns: 1fr; }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .ft-left { flex: 0 0 36%; }
  .ft-left__content { padding: 40px; }
  .ft-left__title { font-size: 1.4rem; }
}
</style>
